import { UseFormReturn } from "react-hook-form";

import Form from "../base/Form/Form";
import { Button } from "../base/Button";
import { FormErrors } from "../base/Form/FormErrors";
import { FormToggle } from "../base/Form/FormToggle";
import { FormSelect } from "../base/Form/FormSelect";
import { FormTextField } from "../base/Form/FormTextField";
import Modal, { ModalControlProps } from "../../layouts/Modal";
import { InputType, SettingType } from "../../models/CustomerSettings/CustomerSetting";
import { ISettingDefinition } from "../../models/SettingDefinition/SettingDefinition";
import { CreateUpdateSettingDefinition } from "../../models/SettingDefinition/CreateUpdateSettingDefinition";
import useSettingDefinitionMutations from "../../data/SettingDefinition/useSettingDefinitionMutations";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";

interface CreateUpdateSettingDefinitionModalProps extends ModalControlProps {
  definition?: ISettingDefinition;
}

export function CreateUpdateSettingDefinitionModal({
  open,
  setOpen,
  definition,
}: CreateUpdateSettingDefinitionModalProps) {
  const { create, update } = useSettingDefinitionMutations();
  const { saveHandlers } = useDefaultCRUDHandlers("Setting");

  const onSubmit = (data: CreateUpdateSettingDefinition) => {
    const hack: any = data;
    const payload: CreateUpdateSettingDefinition = {
      ...hack,
      settingType: parseInt(hack.settingType),
      inputType: parseInt(hack.inputType),
    };

    const mutation = definition ? update : create;

    mutation.mutate(payload, {
      onSuccess: () => {
        saveHandlers.onSuccess();
        setOpen(false);
      },
      onError: () => {
        saveHandlers.onError();
      },
    });
  };

  return (
    <Modal open={open} setOpen={setOpen} width="!w-1/3">
      <div>
        <div className="border-b">
          <h2 className="text-lg font-stick font-medium leading-6 mb-4 text-gray-900">
            {definition ? "Edit" : "Add"} Setting
          </h2>
        </div>
        <Form<CreateUpdateSettingDefinition>
          defaultValues={definition ? definition : {}}
          onSubmit={onSubmit}
          className="grid grid-cols-1 gap-y-4 md:grid-cols-2 md:gap-4"
        >
          {({ register, control }: UseFormReturn<CreateUpdateSettingDefinition>) => (
            <>
              <div className="-m-4 md:col-span-2">
                <FormErrors />
              </div>
              <div className="mb-4 grid grid-cols-1 gap-y-4 md:col-span-2">
                <div>
                  <FormTextField {...register("key", { required: true })} label="Key" />
                  <p className="text-xs text-gray-500 mt-2">
                    {"Use the format <GROUP>:<KEY> if you want to add the Setting to a group."}
                  </p>
                </div>

                <div>
                  <FormTextField {...register("label")} label="Label" />
                </div>

                <div>
                  <FormTextField {...register("description")} label="Description" />
                </div>

                <div>
                  <FormSelect {...register("inputType")} label="Input Type">
                    {enumToSelectOptions(InputType).map((opt) => (
                      <option key={opt.label} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </FormSelect>
                </div>

                <div>
                  <FormSelect {...register("settingType")} label="Setting Type">
                    <option value={SettingType.General}>General</option>
                    <option value={SettingType.Shipping}>Shipping</option>
                  </FormSelect>
                </div>

                <div className="mt-2 flex justify-between space-x-2">
                  <FormToggle
                    {...register("defaultVisibleToCustomer")}
                    control={control}
                    toggleProps={{ label: "Visible to Customer by default?" }}
                  />
                  <FormToggle
                    {...register("required")}
                    control={control}
                    toggleProps={{ label: "Required?" }}
                  />
                  <FormToggle
                    {...register("isArchived")}
                    control={control}
                    toggleProps={{ label: "Archived?" }}
                  />
                </div>
              </div>
              <Button type="submit">Save</Button>
              <Button type="reset" variant="outlined">
                Reset
              </Button>
            </>
          )}
        </Form>
      </div>
    </Modal>
  );
}
